/**
 * Delivery Guarantee (Insurance) safety:
 * 1) Auto-enable #sca-cart-addon only after cart has confirmed merchandise (debounced)
 * 2) Sync protection tier to merchandise-only price table when cart grows/shrinks
 * 3) Keep SCA widget label (.product-price-sca) aligned with merchandise-only tier
 * 4) Deduplicate multiple protection lines (serialized remove → confirm empty → re-add)
 * 5) Block checkout / strip cart when only Delivery Guarantee remains
 *
 * Note: Route SCA widget can price off cart TOTAL (including protection), which
 * causes display thrash ($7.95 ↔ $8.15). Expected tier is always merchandise-only.
 *
 * Non-USD presentment: PROTECTION_TIERS is USD-only. Tier sync is skipped when
 * cart.currency !== USD; Route/SCA owns local-currency pricing. We still dedupe
 * and strip protection-only carts.
 *
 * Critical protocol: never toggle SCA ON unless a fresh cart has merchandise and
 * zero protection lines. Never force-enable after failed sync / duplicates.
 */
(function () {
  const USER_DISABLED_KEY = 'shippingProtectionUserDisabled';
  const DEBOUNCE_MS = 600;
  const SYNC_DEBOUNCE_MS = 900;
  const TIER_TOLERANCE = 0.02;
  const MAX_SYNC_ATTEMPTS = 3;
  const REMOVE_POLL_MS = 400;
  const REMOVE_TIMEOUT_MS = 5000;
  const NOTICE =
    'Your products were missing from the cart. Delivery Guarantee can’t be purchased alone — please add your items again.';

  // Price applies when merchandise subtotal is strictly greater than rangeStart.
  // rangeStart null = base tier.
  const PROTECTION_TIERS = [
    { price: 0.98, rangeStart: null },
    { price: 2.75, rangeStart: 97.86 },
    { price: 2.95, rangeStart: 104.85 },
    { price: 3.15, rangeStart: 111.84 },
    { price: 3.35, rangeStart: 125.82 },
    { price: 3.55, rangeStart: 132.81 },
    { price: 3.75, rangeStart: 139.8 },
    { price: 3.95, rangeStart: 146.79 },
    { price: 4.15, rangeStart: 153.78 },
    { price: 4.35, rangeStart: 167.76 },
    { price: 4.55, rangeStart: 174.75 },
    { price: 4.75, rangeStart: 181.74 },
    { price: 4.95, rangeStart: 195.72 },
    { price: 5.15, rangeStart: 202.71 },
    { price: 5.35, rangeStart: 209.7 },
    { price: 5.55, rangeStart: 223.68 },
    { price: 5.75, rangeStart: 230.67 },
    { price: 5.95, rangeStart: 237.66 },
    { price: 6.15, rangeStart: 251.64 },
    { price: 6.35, rangeStart: 258.63 },
    { price: 6.55, rangeStart: 265.62 },
    { price: 6.75, rangeStart: 272.61 },
    { price: 6.95, rangeStart: 286.59 },
    { price: 7.15, rangeStart: 293.58 },
    { price: 7.35, rangeStart: 300.57 },
    { price: 7.55, rangeStart: 314.55 },
    { price: 7.75, rangeStart: 321.54 },
    { price: 7.95, rangeStart: 328.53 },
    { price: 8.15, rangeStart: 342.51 },
    { price: 8.35, rangeStart: 349.5 },
    { price: 8.55, rangeStart: 356.49 },
    { price: 8.75, rangeStart: 363.48 },
    { price: 8.95, rangeStart: 377.46 },
    { price: 9.38, rangeStart: 384.45 },
    { price: 10.03, rangeStart: 391.44 },
    { price: 10.68, rangeStart: 405.42 },
    { price: 11.33, rangeStart: 412.41 },
    { price: 11.98, rangeStart: 419.4 },
    { price: 12.63, rangeStart: 433.38 },
    { price: 13.28, rangeStart: 440.37 },
    { price: 14.58, rangeStart: 447.36 },
    { price: 15.23, rangeStart: 461.34 },
    { price: 15.88, rangeStart: 468.33 },
    { price: 16.53, rangeStart: 475.32 },
    { price: 17.18, rangeStart: 482.31 },
    { price: 17.83, rangeStart: 489.3 },
    { price: 18.48, rangeStart: 496.29 },
    { price: 19.13, rangeStart: 503.28 },
    { price: 19.78, rangeStart: 510.27 },
    { price: 20.43, rangeStart: 517.26 },
    { price: 24.38, rangeStart: 531.24 },
    { price: 31.63, rangeStart: 538.23 },
    { price: 38.88, rangeStart: 545.22 },
    { price: 46.13, rangeStart: 559.2 },
    { price: 53.38, rangeStart: 566.19 },
    { price: 60.63, rangeStart: 573.18 },
    { price: 67.88, rangeStart: 587.16 },
    { price: 75.13, rangeStart: 594.15 },
    { price: 82.38, rangeStart: 601.14 },
    { price: 89.63, rangeStart: 615.12 },
    { price: 96.88, rangeStart: 622.11 },
    { price: 104.13, rangeStart: 629.1 },
    { price: 111.38, rangeStart: 643.08 },
    { price: 118.63, rangeStart: 650.07 },
    { price: 125.88, rangeStart: 657.06 },
    { price: 133.13, rangeStart: 671.04 },
    { price: 140.38, rangeStart: 678.03 },
  ];

  let enableTimer = null;
  let syncTimer = null;
  let mutationBusy = false;
  let syncChain = Promise.resolve();
  let syncAttempts = 0;
  let allowNextCheckout = false;
  let programmaticToggle = false;
  let widgetObserver = null;
  let widgetSyncBusy = false;

  function isProtectionItem(item) {
    if (!item) return false;
    const title = String(item.product_title || item.title || '').toLowerCase();
    const type = String(item.product_type || item.type || '').toLowerCase();
    const tags = item.product?.tags || item.tags || [];
    const tagList = Array.isArray(tags)
      ? tags.map((t) => String(t).toLowerCase())
      : String(tags).toLowerCase().split(/,\s*/);

    if (type === 'insurance') return true;
    if (title.includes('delivery guarantee')) return true;
    if (title.includes('shipping protection')) return true;
    if (
      tagList.includes('block-from-reorder') &&
      (type === 'insurance' || title.includes('guarantee') || title.includes('protection'))
    ) {
      return true;
    }
    return false;
  }

  function getCartFromEvent(event) {
    return event?.detail?.cart?.cart || event?.detail?.cart || null;
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
  }

  function merchandiseItems(cart) {
    return (cart?.items || []).filter((item) => !isProtectionItem(item));
  }

  function protectionItems(cart) {
    return (cart?.items || []).filter((item) => isProtectionItem(item));
  }

  function merchandiseSubtotal(cart) {
    return merchandiseItems(cart).reduce(function (sum, item) {
      return sum + Number(item.final_line_price || item.line_price || 0);
    }, 0) / 100;
  }

  function getCartCurrency(cart) {
    if (cart && cart.currency) return String(cart.currency).toUpperCase();
    if (window.Shopify && Shopify.currency && Shopify.currency.active) {
      return String(Shopify.currency.active).toUpperCase();
    }
    return 'USD';
  }

  // Tier table is USD-only. Presentment currencies (PHP, EUR, …) must not be
  // compared to USD thresholds — that causes endless sync / cart drawer reopen.
  function isUsdCart(cart) {
    return getCartCurrency(cart) === 'USD';
  }

  function expectedProtectionPriceForCart(cart) {
    if (!isUsdCart(cart)) return null;
    return expectedProtectionPrice(merchandiseSubtotal(cart));
  }

  function expectedProtectionPrice(merchSubtotal) {
    let expected = PROTECTION_TIERS[0].price;
    for (let i = 0; i < PROTECTION_TIERS.length; i++) {
      const tier = PROTECTION_TIERS[i];
      if (tier.rangeStart == null || merchSubtotal > tier.rangeStart) {
        expected = tier.price;
      }
    }
    return expected;
  }

  function isProtectionOnlyCart(cart) {
    const items = cart?.items || [];
    return items.length > 0 && merchandiseItems(cart).length === 0;
  }

  function resolveCart(event) {
    const fromEvent = getCartFromEvent(event);
    if (fromEvent && Array.isArray(fromEvent.items)) {
      return Promise.resolve(fromEvent);
    }
    return fetchCart();
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function getProtectionToggle() {
    return document.querySelector('#sca-cart-addon input[type="checkbox"]');
  }

  function parseWidgetProtectionPrice() {
    const priceEl = document.querySelector('#sca-cart-addon .product-price-sca');
    if (priceEl) {
      const match = priceEl.textContent.match(/([\d,]+(?:\.\d{1,2})?)/);
      if (match) return parseFloat(match[1].replace(/,/g, ''));
    }

    const addon = document.querySelector('#sca-cart-addon');
    if (!addon) return null;

    const match = addon.textContent.match(/delivery guarantee\s*\$?\s*([\d,]+(?:\.\d{1,2})?)/i);
    return match ? parseFloat(match[1].replace(/,/g, '')) : null;
  }

  function formatProtectionPrice(price) {
    return '$' + Number(price).toFixed(2);
  }

  function syncWidgetDisplayPrice(expectedPrice) {
    if (expectedPrice == null || localStorage.getItem(USER_DISABLED_KEY)) return false;

    const formatted = formatProtectionPrice(expectedPrice);
    widgetSyncBusy = true;

    try {
      const priceEl = document.querySelector('#sca-cart-addon .product-price-sca');
      if (priceEl) {
        priceEl.textContent = formatted;
      }

      const addon = document.querySelector('#sca-cart-addon');
      if (addon) {
        const walker = document.createTreeWalker(addon, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (!/delivery guarantee/i.test(node.textContent)) continue;
          node.textContent = node.textContent.replace(
            /(delivery guarantee\s*\$?\s*)([\d,]+(?:\.\d{1,2})?)/i,
            '$1' + expectedPrice.toFixed(2)
          );
        }
      }
    } finally {
      widgetSyncBusy = false;
    }

    ensureWidgetObserver();
    return true;
  }

  function syncWidgetDisplayFromCart(cart) {
    if (localStorage.getItem(USER_DISABLED_KEY)) return;
    if (!isUsdCart(cart)) return;
    if (!merchandiseItems(cart).length || !protectionItems(cart).length) return;
    syncWidgetDisplayPrice(expectedProtectionPrice(merchandiseSubtotal(cart)));
  }

  function ensureWidgetObserver() {
    const addon = document.querySelector('#sca-cart-addon');
    if (!addon) {
      if (widgetObserver) {
        widgetObserver.disconnect();
        widgetObserver = null;
      }
      return;
    }

    if (widgetObserver) return;

    widgetObserver = new MutationObserver(function () {
      if (widgetSyncBusy) return;
      if (localStorage.getItem(USER_DISABLED_KEY)) return;

      const toggle = getProtectionToggle();
      if (!toggle || !toggle.checked) return;

      const displayed = parseWidgetProtectionPrice();
      if (displayed == null) return;

      fetchCart()
        .then(function (cart) {
          if (!isUsdCart(cart)) return;
          if (!protectionItems(cart).length || !merchandiseItems(cart).length) return;
          const expected = expectedProtectionPrice(merchandiseSubtotal(cart));
          if (Math.abs(displayed - expected) > TIER_TOLERANCE) {
            syncWidgetDisplayPrice(expected);
          }
        })
        .catch(function () {});
    });

    widgetObserver.observe(addon, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function pricesMismatch(expectedPrice, linePriceCents) {
    if (expectedPrice == null || linePriceCents == null) return false;
    return Math.abs(expectedPrice - linePriceCents / 100) > TIER_TOLERANCE;
  }

  function setToggleChecked(desired) {
    const toggle = getProtectionToggle();
    if (!toggle) return false;
    if (!!toggle.checked === !!desired) return true;

    const wasProgrammatic = programmaticToggle;
    programmaticToggle = true;
    try {
      toggle.click();
    } finally {
      programmaticToggle = wasProgrammatic;
    }
    return !!toggle.checked === !!desired;
  }

  function withProgrammaticToggle(fn) {
    programmaticToggle = true;
    return Promise.resolve()
      .then(fn)
      .finally(function () {
        programmaticToggle = false;
      });
  }

  function queueMutation(fn) {
    syncChain = syncChain
      .catch(function () {})
      .then(function () {
        mutationBusy = true;
        return Promise.resolve()
          .then(fn)
          .finally(function () {
            mutationBusy = false;
          });
      });
    return syncChain;
  }

  // --- Track manual opt-out ---

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    if (!e.target.matches('#sca-cart-addon input[type="checkbox"]')) return;
    if (programmaticToggle) return;

    if (!e.target.checked) {
      localStorage.setItem(USER_DISABLED_KEY, 'true');
    } else {
      localStorage.removeItem(USER_DISABLED_KEY);
    }
  });

  // --- Safe auto-enable (merchandise required; paused during mutations) ---

  function EnableShippingProtection() {
    if (localStorage.getItem(USER_DISABLED_KEY)) return;
    if (mutationBusy) return;

    clearTimeout(enableTimer);
    enableTimer = setTimeout(function () {
      if (mutationBusy) return;
      if (localStorage.getItem(USER_DISABLED_KEY)) return;

      queueMutation(function () {
        return fetchCart().then(function (cart) {
          if (localStorage.getItem(USER_DISABLED_KEY)) return false;
          if (!cart?.items?.length) return false;
          if (merchandiseItems(cart).length === 0) return false;
          if (protectionItems(cart).length > 0) return false;

          setToggleChecked(true);
          return waitForProtectionLine(expectedProtectionPriceForCart(cart), 4500).then(
            function (updated) {
              syncWidgetDisplayFromCart(updated);
              return true;
            }
          );
        });
      });
    }, DEBOUNCE_MS);
  }

  document.addEventListener('rebuy:cart.add', EnableShippingProtection);
  document.addEventListener('rebuy:cart.viewed', EnableShippingProtection);

  // --- Protection-only guard ---

  function showNotice() {
    try {
      window.alert(NOTICE);
    } catch (e) {}
  }

  function removeProtectionLines(cart) {
    const lines = protectionItems(cart);
    if (!lines.length) return Promise.resolve(cart);

    const updates = {};
    lines.forEach(function (item) {
      // Prefer line-item key so duplicate same-variant lines are all cleared.
      const key = item.key || String(item.variant_id || item.id);
      updates[String(key)] = 0;
      if (item.variant_id != null) {
        updates[String(item.variant_id)] = 0;
      }
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      return r.json();
    });
  }

  function waitUntilProtectionGone(timeoutMs) {
    const started = Date.now();
    const timeout = timeoutMs || REMOVE_TIMEOUT_MS;

    function poll() {
      return fetchCart().then(function (cart) {
        if (protectionItems(cart).length === 0) {
          return { ok: true, cart: cart };
        }
        if (Date.now() - started >= timeout) {
          return { ok: false, cart: cart };
        }
        return delay(REMOVE_POLL_MS).then(poll);
      });
    }

    return poll();
  }

  function waitForProtectionLine(expectedPrice, timeoutMs) {
    const started = Date.now();
    const timeout = timeoutMs || 4000;

    function poll() {
      return fetchCart().then(function (cart) {
        const prot = protectionItems(cart);
        if (
          prot.length === 1 &&
          (expectedPrice == null || !pricesMismatch(expectedPrice, prot[0].price))
        ) {
          return cart;
        }
        if (Date.now() - started >= timeout) {
          return cart;
        }
        return delay(400).then(poll);
      });
    }

    return poll();
  }

  function normalizeProtectionQuantities(cart) {
    const prot = protectionItems(cart);
    const updates = {};
    let needsUpdate = false;

    prot.forEach(function (item) {
      if (Number(item.quantity) > 1) {
        updates[String(item.key || item.variant_id || item.id)] = 1;
        needsUpdate = true;
      }
    });

    if (!needsUpdate) return Promise.resolve(cart);

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * Safe replace: remove all protection, confirm gone, then toggle ON once
   * only if merchandise is still present. Never force-enable on failure.
   */
  function refreshProtectionAddon(expectedPrice) {
    return withProgrammaticToggle(function () {
      const toggle = getProtectionToggle();
      if (!toggle) return false;

      return fetchCart()
        .then(function (cart) {
          if (!merchandiseItems(cart).length) {
            setToggleChecked(false);
            return removeProtectionLines(cart).then(function () {
              return false;
            });
          }

          setToggleChecked(false);
          return removeProtectionLines(cart)
            .then(function () {
              return waitUntilProtectionGone(REMOVE_TIMEOUT_MS);
            })
            .then(function (result) {
              if (!result.ok) {
                // Still have protection after timeout — strip again and abort re-add.
                return removeProtectionLines(result.cart)
                  .then(function () {
                    return waitUntilProtectionGone(2000);
                  })
                  .then(function () {
                    setToggleChecked(false);
                    return false;
                  });
              }

              return fetchCart().then(function (fresh) {
                if (!merchandiseItems(fresh).length) {
                  setToggleChecked(false);
                  return false;
                }
                if (protectionItems(fresh).length > 0) {
                  setToggleChecked(false);
                  return false;
                }
                if (localStorage.getItem(USER_DISABLED_KEY)) {
                  setToggleChecked(false);
                  return false;
                }

                setToggleChecked(true);
                return waitForProtectionLine(expectedPrice, 4500).then(function (updated) {
                  const prot = protectionItems(updated);
                  if (prot.length !== 1) {
                    // Duplicate or missing — strip all and leave off rather than retry-enable.
                    return removeProtectionLines(updated)
                      .then(function () {
                        return waitUntilProtectionGone(3000);
                      })
                      .then(function () {
                        setToggleChecked(false);
                        return false;
                      });
                  }
                  return true;
                });
              });
            });
        });
    });
  }

  function abortSyncLeaveOff(cart) {
    syncAttempts = 0;
    setToggleChecked(false);
    return removeProtectionLines(cart || { items: [] })
      .then(function () {
        return waitUntilProtectionGone(3000);
      })
      .then(function () {
        return false;
      });
  }

  function syncProtectionTier(options) {
    const opts = options || {};
    if (localStorage.getItem(USER_DISABLED_KEY)) return Promise.resolve(false);

    return fetchCart()
      .then(function (cart) {
        if (isProtectionOnlyCart(cart)) {
          return removeProtectionLines(cart)
            .then(function () {
              return waitUntilProtectionGone(3000);
            })
            .then(function () {
              setToggleChecked(false);
              return false;
            });
        }

        if (!merchandiseItems(cart).length) {
          setToggleChecked(false);
          return false;
        }

        return normalizeProtectionQuantities(cart).then(function (normalized) {
          const current = normalized || cart;
          const prot = protectionItems(current);
          const expected = expectedProtectionPriceForCart(current);

          if (!isUsdCart(current)) {
            syncAttempts = 0;
            if (prot.length > 1) {
              syncAttempts += 1;
              if (syncAttempts > MAX_SYNC_ATTEMPTS) {
                console.warn(
                  '[DeliveryGuaranteeGuard] max sync attempts with duplicates (non-USD) — stripping protection'
                );
                return abortSyncLeaveOff(current);
              }
              return refreshProtectionAddon(null).then(function () {
                return fetchCart().then(function (updatedCart) {
                  if (protectionItems(updatedCart).length === 1) {
                    syncAttempts = 0;
                    setToggleChecked(true);
                    return true;
                  }
                  if (syncAttempts < MAX_SYNC_ATTEMPTS) {
                    return syncProtectionTier({ force: true });
                  }
                  return abortSyncLeaveOff(updatedCart);
                });
              });
            }
            if (prot.length === 1) {
              setToggleChecked(true);
              return false;
            }
            return false;
          }

          if (prot.length > 1) {
            syncAttempts += 1;
            if (syncAttempts > MAX_SYNC_ATTEMPTS) {
              console.warn('[DeliveryGuaranteeGuard] max sync attempts with duplicates — stripping protection');
              return abortSyncLeaveOff(current);
            }
            return refreshProtectionAddon(expected).then(function (ok) {
              return fetchCart().then(function (updatedCart) {
                if (!merchandiseItems(updatedCart).length) {
                  return abortSyncLeaveOff(updatedCart);
                }
                const updatedProt = protectionItems(updatedCart);
                const updatedExpected = expectedProtectionPrice(merchandiseSubtotal(updatedCart));
                if (
                  updatedProt.length === 1 &&
                  !pricesMismatch(updatedExpected, updatedProt[0].price)
                ) {
                  syncAttempts = 0;
                  return true;
                }
                if (syncAttempts < MAX_SYNC_ATTEMPTS) {
                  return syncProtectionTier({ force: true });
                }
                return abortSyncLeaveOff(updatedCart);
              });
            });
          }

          if (!prot.length) {
            syncAttempts += 1;
            if (syncAttempts > MAX_SYNC_ATTEMPTS) {
              console.warn('[DeliveryGuaranteeGuard] max sync attempts — leaving protection off');
              setToggleChecked(false);
              syncAttempts = 0;
              return false;
            }
            return refreshProtectionAddon(expected).then(function () {
              return fetchCart().then(function (updatedCart) {
                if (!merchandiseItems(updatedCart).length) {
                  return abortSyncLeaveOff(updatedCart);
                }
                const updatedProt = protectionItems(updatedCart);
                if (updatedProt.length === 1) {
                  syncAttempts = 0;
                  return true;
                }
                if (updatedProt.length > 1) {
                  return abortSyncLeaveOff(updatedCart);
                }
                if (syncAttempts < MAX_SYNC_ATTEMPTS) {
                  return syncProtectionTier({ force: true });
                }
                setToggleChecked(false);
                syncAttempts = 0;
                return false;
              });
            });
          }

          if (!pricesMismatch(expected, prot[0].price)) {
            syncAttempts = 0;
            setToggleChecked(true);
            return false;
          }

          syncAttempts += 1;
          if (syncAttempts > MAX_SYNC_ATTEMPTS) {
            console.warn('[DeliveryGuaranteeGuard] max sync attempts with wrong tier — stripping');
            return abortSyncLeaveOff(current);
          }

          return refreshProtectionAddon(expected).then(function () {
            return fetchCart().then(function (updatedCart) {
              if (!merchandiseItems(updatedCart).length) {
                return abortSyncLeaveOff(updatedCart);
              }
              const updatedProt = protectionItems(updatedCart);
              const updatedExpected = expectedProtectionPrice(merchandiseSubtotal(updatedCart));

              if (
                updatedProt.length === 1 &&
                !pricesMismatch(updatedExpected, updatedProt[0].price)
              ) {
                syncAttempts = 0;
                setToggleChecked(true);
                return true;
              }

              if (updatedProt.length > 1) {
                return abortSyncLeaveOff(updatedCart);
              }

              if (syncAttempts < MAX_SYNC_ATTEMPTS) {
                return syncProtectionTier({ force: true });
              }

              return abortSyncLeaveOff(updatedCart);
            });
          });
        });
      })
      .catch(function (err) {
        console.warn('[DeliveryGuaranteeGuard] sync failed', err);
        setToggleChecked(false);
        return false;
      })
      .finally(function () {
        fetchCart()
          .then(function (cart) {
            syncWidgetDisplayFromCart(cart);
          })
          .catch(function () {});
      });
  }

  function queueProtectionSync(options) {
    return queueMutation(function () {
      return syncProtectionTier(options || {}).then(function (result) {
        return fetchCart().then(function (cart) {
          syncWidgetDisplayFromCart(cart);
          return result;
        });
      });
    });
  }

  function scheduleProtectionSync() {
    if (localStorage.getItem(USER_DISABLED_KEY)) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      queueProtectionSync();
    }, SYNC_DEBOUNCE_MS);
  }

  function enforceProtectionOnlyGuard(options) {
    const opts = options || {};

    return queueMutation(function () {
      return fetchCart().then(function (cart) {
        if (!isProtectionOnlyCart(cart)) return false;

        setToggleChecked(false);
        return removeProtectionLines(cart)
          .then(function () {
            return waitUntilProtectionGone(REMOVE_TIMEOUT_MS);
          })
          .then(function () {
            if (opts.notify !== false) showNotice();
            try {
              window.Rebuy?.SmartCart?.hide?.();
            } catch (e) {}
            return true;
          });
      });
    }).catch(function () {
      return false;
    });
  }

  function findCheckoutControl(el) {
    if (!(el instanceof Element)) return null;
    return (
      el.closest('button[name="checkout"], input[name="checkout"]') ||
      el.closest('a[href*="/checkout"]') ||
      el.closest('.rebuy-cart__checkout-button, .rebuy-button--checkout, [data-action="checkout"]') ||
      null
    );
  }

  function isExpressCheckoutControl(el) {
    if (!(el instanceof Element)) return false;
    return !!(
      el.closest('.additional-checkout-buttons') ||
      el.closest('.cart__dynamic-checkout-buttons') ||
      el.closest('shopify-accelerated-checkout') ||
      el.closest('[data-shopify-buttoncontainer]') ||
      el.closest('.shopify-payment-button')
    );
  }

  function isCheckoutIntent(el) {
    if (!(el instanceof Element)) return false;
    if (findCheckoutControl(el)) return true;
    if (isExpressCheckoutControl(el)) return true;

    const control = el.closest('button, a, input[type="submit"]');
    if (!control) return false;
    const text = (control.textContent || control.value || '').trim().toLowerCase();
    return text === 'check out' || text === 'checkout' || text === 'secure checkout';
  }

  function proceedCheckout(control) {
    allowNextCheckout = true;

    if (control && control.tagName === 'A' && control.href) {
      window.location.href = control.href;
      return;
    }

    if (control && control.form && (control.name === 'checkout' || control.type === 'submit')) {
      HTMLFormElement.prototype.submit.call(control.form);
      return;
    }

    window.location.href = '/checkout';
  }

  function prepareCheckoutThen(control) {
    // Strip protection-only first; only then sync tier if merchandise remains.
    return enforceProtectionOnlyGuard({ notify: true })
      .then(function (blocked) {
        if (blocked) {
          window.location.href = '/collections/all';
          return;
        }
        return fetchCart().then(function (cart) {
          if (!merchandiseItems(cart).length) {
            window.location.href = '/collections/all';
            return;
          }
          return queueProtectionSync({ force: true }).then(function () {
            return fetchCart().then(function (synced) {
              if (isProtectionOnlyCart(synced) || !merchandiseItems(synced).length) {
                return enforceProtectionOnlyGuard({ notify: true }).then(function () {
                  window.location.href = '/collections/all';
                });
              }
              // If sync left duplicates, strip protection rather than charge twice.
              if (protectionItems(synced).length > 1) {
                return abortSyncLeaveOff(synced).then(function () {
                  proceedCheckout(control);
                });
              }
              proceedCheckout(control);
            });
          });
        });
      });
  }

  document.addEventListener(
    'click',
    function (e) {
      if (allowNextCheckout) {
        allowNextCheckout = false;
        return;
      }
      if (!isCheckoutIntent(e.target)) return;

      const control =
        findCheckoutControl(e.target) ||
        e.target.closest('button, a, input[type="submit"]') ||
        e.target;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      prepareCheckoutThen(control);
    },
    true
  );

  function onCartEvent(event) {
    resolveCart(event).then(function (cart) {
      if (isProtectionOnlyCart(cart)) {
        // Cancel pending enable/sync — strip immediately.
        clearTimeout(enableTimer);
        clearTimeout(syncTimer);
        enforceProtectionOnlyGuard({ notify: false });
        return;
      }
      scheduleProtectionSync();
      syncWidgetDisplayFromCart(cart);
      ensureWidgetObserver();
    });
  }

  document.addEventListener('rebuy:cart.add', onCartEvent);
  document.addEventListener('rebuy:cart.change', onCartEvent);
  document.addEventListener('rebuy:cart.viewed', onCartEvent);

  if (/\/cart(\/|$|\?)/.test(window.location.pathname)) {
    enforceProtectionOnlyGuard({ notify: true });
    scheduleProtectionSync();
  }

  if (/\/checkouts?\b/.test(window.location.pathname)) {
    enforceProtectionOnlyGuard({ notify: true }).then(function (blocked) {
      if (blocked) window.location.href = '/collections/all';
    });
  }

  window.DeliveryGuaranteeGuard = {
    isProtectionItem: isProtectionItem,
    enforce: enforceProtectionOnlyGuard,
    syncProtectionTier: function (options) {
      return queueProtectionSync(options);
    },
    syncWidgetDisplayPrice: syncWidgetDisplayPrice,
    parseWidgetProtectionPrice: parseWidgetProtectionPrice,
    expectedProtectionPrice: expectedProtectionPrice,
    waitUntilProtectionGone: waitUntilProtectionGone,
  };
})();
